import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Button, Dropdown, InputItem } from "pepsico-ds";
import { useState } from "react";

/**
 * Configuration-driven filters for locally loaded table data.
 * Filter values remain owned by the consuming table and never enter outcome data.
 */
const TableFilterBar = ({
  filters = {},
  fields = [],
  onChange,
  onClear,
  clearButtonVariant = "tertiary",
}) => {
  const [resetKey, setResetKey] = useState(0);
  const hasActiveFilters = Object.values(filters).some(Boolean);

  const handleClear = () => {
    setResetKey((prev) => prev + 1);
    onClear?.();
  };

  return (
    <div className="filter-row">
      {fields.map((field) => {
        const value = filters[field.key] ?? "";
        const controlStyle = field.width
          ? { width: field.width, minWidth: field.width }
          : undefined;

        if (field.type === "select") {
          const childList = (field.options || []).map((option) => ({
            id: option.value,
            displayText: option.label,
          }));
          const selectedValue = value
            ? childList.filter((option) => option.id === value)
            : undefined;

          return (
            <div className="filter-control" key={field.key} style={controlStyle}>
              <Dropdown
                key={`${field.key}-${value || "empty"}-${resetKey}`}
                childList={childList}
                label={field.label}
                placeholder={
                  field.placeholder ||
                  field.emptyOptionLabel ||
                  `Select ${field.label}`
                }
                selection="single"
                selectedValue={selectedValue}
                setSelectedValue={(selected) =>
                  onChange(field.key, selected?.[0]?.id || "")
                }
                size="small"
                isClearable
                style={{ width: "100%" }}
              />
            </div>
          );
        }

        if (field.type === "date") {
          return (
            <div className="filter-control" key={field.key} style={controlStyle}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  key={`${field.key}-${value || "empty"}-${resetKey}`}
                  label={field.label}
                  value={value ? moment(value) : null}
                  onChange={(selected) =>
                    onChange(
                      field.key,
                      selected?.isValid() ? selected.format("YYYY-MM-DD") : ""
                    )
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: field.placeholder || "mm/dd/yyyy",
                      style: { width: "100%" },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          );
        }

        const inputId = `table-filter-${field.key}`;

        return (
          <div
            className="filter-control filter-text-control"
            key={field.key}
            style={controlStyle}
          >
            <label className="filter-control-label" htmlFor={inputId}>
              {field.label}
            </label>
            <InputItem
              key={`${field.key}-${resetKey}`}
              id={inputId}
              aria-label={field.label}
              inputSize="small"
              value={value}
              placeholder={field.placeholder}
              style={{ width: "100%" }}
              onChange={(event) =>
                onChange(
                  field.key,
                  typeof event === "string" ? event : (event?.target?.value ?? "")
                )
              }
            />
          </div>
        );
      })}

      {hasActiveFilters && (
        <Button size="small" variant={clearButtonVariant} onClick={handleClear}>
          Clear filters
        </Button>
      )}
    </div>
  );
};

export default TableFilterBar;
